import { ReactElement } from 'react'

import { Trans, useTranslation } from 'react-i18next'

import { useAppConfig } from 'appConfig'
import { Flex } from 'components/core'
import { ContactUsLink } from 'components/Links'
import { PageHeader } from 'components/PageHeader'
import { formatDay } from 'helpers/formatting'
import CobaltLogoMark from 'images/logo/CobaltLogoMark'
import { Organization } from 'types'

import * as styles from './styles.scss'

interface Props {
    organization: Organization
}

function OrganizationLockedPage({ organization }: Props): ReactElement {
    const { t } = useTranslation('account')
    const { productTitle } = useAppConfig()

    const formattedDateMessage = organization.trial_expiration_date
        ? `${t('account:OrganizationLockedPage.trialEndedOn', ' on')} ${formatDay(organization.trial_expiration_date)}`
        : ''
    const isLocked = organization.locked

    return (
        <div className={styles.root}>
            <div className={styles.headerWrap}>
                <PageHeader />
            </div>
            <Flex
                className={styles.contentOverlay}
                justifyContent="center"
                alignItems="center"
            >
                <div className={styles.card}>
                    <CobaltLogoMark fill="#FFF" style={{ height: 42 }} />
                    <h1 className={styles.header}>
                        {isLocked
                            ? t(
                                  'account:OrganizationLockedPage.header',
                                  'Thanks for using {{ productTitle }}!',
                                  { productTitle }
                              )
                            : t(
                                  'account:OrganizationLockedPage.headerDisabled',
                                  'Account Suspended'
                              )}
                    </h1>

                    <div className={styles.cta}>
                        {isLocked ? (
                            <Trans i18nKey="account:OrganizationLockedPage.message">
                                Your service ended{{ formattedDateMessage }}. To
                                continue using {{ productTitle }}{' '}
                                <ContactUsLink
                                    authenticated
                                    link
                                    contactUsPage="/group/3/create/10"
                                >
                                    contact us
                                </ContactUsLink>
                                .
                            </Trans>
                        ) : (
                            <Trans i18nKey="account:OrganizationLockedPage.messageDisabled">
                                Your subscription is{' '}
                                <strong>currently past due</strong>. Please
                                remit payment as soon as possible to avoid any
                                interruption in service.
                                <br />
                                To connect with our Accounting team, please
                                email us at{' '}
                                <a href="mailto:accounting@junglescout.com">
                                    accounting@junglescout.com
                                </a>
                                .
                            </Trans>
                        )}
                    </div>

                    <p className="fg-text-extra-light">
                        {isLocked
                            ? t(
                                  'account:OrganizationLockedPage.footer',
                                  '*Your Amazon Integration data is no longer being synced, enabled automations have been paused and any active dayparting bids have been reverted to base bids.'
                              )
                            : t(
                                  'account:OrganizationLockedPage.footerDisabled',
                                  '*We will keep all data and automations for thirty (30) days from the suspension date before permanently deleting it.'
                              )}
                    </p>
                </div>
            </Flex>
        </div>
    )
}

export default OrganizationLockedPage
